import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { t } from '../i18n';
import { api, ApiError } from '../lib/api';
import { fmtDate, fmtMoney } from '../lib/format';
import type { DocumentType, Duplicate, Ingestion, PaymentTerms } from '../lib/types';
import { Button, Callout, Checkbox, Confidence, Field, Input, Ltr, Select, useConfirm, useToast } from './ui';
import { CustomerPicker } from './CustomerPicker';

type Mode = 'candidate' | 'other' | 'new';

const DOC_TYPES: DocumentType[] = ['tax_invoice', 'tax_invoice_receipt', 'receipt', 'credit_note', 'delivery_note', 'quote', 'proforma', 'other'];

/**
 * Confirm / correct a detected document and attach it to a customer.
 * Used by the review queue and by manual upload.
 */
export function ResolveForm({
  ingestion,
  presetCustomerId,
  onDone,
  onIgnore,
}: {
  ingestion: Ingestion;
  presetCustomerId?: number | null;
  onDone: (r: { documentId: number; customerId: number }) => void;
  onIgnore?: () => void;
}) {
  const ex = ingestion.extracted;
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const firstCandidate = ingestion.candidates[0]?.customerId ?? null;
  const [mode, setMode] = useState<Mode>(presetCustomerId ? 'other' : firstCandidate ? 'candidate' : 'new');
  const [candidate, setCandidate] = useState<number | null>(firstCandidate);
  const [other, setOther] = useState<number | null>(presetCustomerId ?? null);
  const [newC, setNewC] = useState(() => initialNew(ingestion));
  const [fields, setFields] = useState(() => initialFields(ingestion));
  const [learnAlias, setLearnAlias] = useState(true);
  const [setCN, setSetCN] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dups, setDups] = useState<Duplicate[] | null>(null);

  useEffect(() => {
    // New item selected in the queue: reset everything.
    const fc = ingestion.candidates[0]?.customerId ?? null;
    setMode(presetCustomerId ? 'other' : fc ? 'candidate' : 'new');
    setCandidate(fc);
    setOther(presetCustomerId ?? null);
    setNewC(initialNew(ingestion));
    setFields(initialFields(ingestion));
    setError(null);
    setDups(null);
  }, [ingestion, presetCustomerId]);

  const customerId = mode === 'candidate' ? candidate : mode === 'other' ? other : null;
  const nums = ['subtotal', 'vat', 'total'] as const;
  const badNumber = nums.some((k) => fields[k].trim() !== '' && Number.isNaN(Number(fields[k].replace(/,/g, ''))));
  const canSubmit =
    !busy &&
    !badNumber &&
    (mode === 'new' ? newC.businessName.trim().length >= 2 && (newC.paymentTerms !== 'EOM_FIXED_DAY' || Number(newC.paymentDay) >= 1) : Boolean(customerId));

  const sumCheck = useMemo(() => {
    const s = Number(fields.subtotal);
    const v = Number(fields.vat);
    const tot = Number(fields.total);
    if (!fields.subtotal || !fields.vat || !fields.total || [s, v, tot].some(Number.isNaN)) return null;
    return Math.abs(s + v - tot) > 0.02 ? `לפני מע״מ (${fmtMoney(s)}) + מע״מ (${fmtMoney(v)}) ≠ סה״כ (${fmtMoney(tot)})` : null;
  }, [fields]);

  const submit = async (confirmDuplicate = false) => {
    setBusy(true);
    setError(null);
    const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(/,/g, '')));
    try {
      const body = {
        ...(mode === 'new'
          ? {
              newCustomer: {
                customer: {
                  businessName: newC.businessName.trim(),
                  companyNumber: newC.companyNumber.trim() || null,
                  paymentTerms: newC.paymentTerms,
                  paymentDay: newC.paymentTerms === 'EOM_FIXED_DAY' ? Number(newC.paymentDay) : null,
                  parentId: newC.parentId,
                  status: 'active',
                },
                addresses: ex?.customerAddress ? [{ label: 'כתובת מהמסמך', street: ex.customerAddress, isPrimary: true }] : [],
                confirmDuplicate,
              },
            }
          : { customerId }),
        fields: {
          type: fields.type,
          number: fields.number.trim() || null,
          issueDate: fields.issueDate || null,
          subtotal: num(fields.subtotal),
          vat: num(fields.vat),
          total: num(fields.total),
          description: fields.description.trim() || null,
          deliveryAddress: fields.type === 'delivery_note' ? fields.deliveryAddress.trim() || null : null,
        },
        learnAlias,
        setCompanyNumber: setCN,
      };
      const r = await api<{ documentId: number; customerId: number }>(`/api/ingestions/${ingestion.id}/resolve`, { body });
      for (const k of ['ingestions', 'agent-status', 'documents', 'customer', 'customers', 'customer-options', 'dashboard', 'cashflow']) qc.invalidateQueries({ queryKey: [k] });
      toast.success(t.review.assigned);
      onDone(r);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && (e.details as { duplicates?: Duplicate[] })?.duplicates) {
        setDups((e.details as { duplicates: Duplicate[] }).duplicates);
      } else {
        setError(e instanceof ApiError ? e.message : t.common.unknownError);
      }
    } finally {
      setBusy(false);
    }
  };

  const ignore = async () => {
    if (!(await confirm({ title: t.review.ignoreConfirm, confirmLabel: t.review.ignore }))) return;
    try {
      await api(`/api/ingestions/${ingestion.id}/ignore`, { method: 'POST' });
      qc.invalidateQueries({ queryKey: ['ingestions'] });
      qc.invalidateQueries({ queryKey: ['agent-status'] });
      onIgnore?.();
    } catch (e) {
      toast.error(e);
    }
  };

  const set = (k: keyof typeof fields) => (e: { target: { value: string } }) => setFields({ ...fields, [k]: e.target.value });
  const warnings = [...(ex?.warnings ?? [])];

  return (
    <div className="stack">
      {error ? <Callout tone="bad">{error}</Callout> : null}
      {warnings.length ? (
        <Callout tone="warn" title={t.review.warnings}>
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      <section>
        <h3 style={{ marginBottom: 8 }}>{t.review.detected}</h3>
        <dl className="kv">
          <dt>שם הלקוח במסמך</dt>
          <dd className="strong">{ex?.customerName ?? <span className="muted">לא זוהה</span>}</dd>
          <dt>ח.פ / ע.מ</dt>
          <dd>{ex?.companyNumber ? <Ltr className="tabular">{ex.companyNumber}</Ltr> : <span className="muted">לא מופיע במסמך</span>}</dd>
          {ex?.customerAddress ? (
            <>
              <dt>כתובת</dt>
              <dd>{ex.customerAddress}</dd>
            </>
          ) : null}
          {ex?.issueDate ? (
            <>
              <dt>תאריך המסמך</dt>
              <dd className="tabular">{fmtDate(ex.issueDate)}{ex.dueDate ? ` · לתשלום עד ${fmtDate(ex.dueDate)}` : ''}</dd>
            </>
          ) : null}
          {ex?.references.length ? (
            <>
              <dt>מתייחס ל</dt>
              <dd>{ex.references.map((r) => `${t.enums.docType[r.type]} ${r.number}`).join(', ')}</dd>
            </>
          ) : null}
          {ingestion.matchReasons.length ? (
            <>
              <dt>תוצאת הזיהוי</dt>
              <dd className="muted">{ingestion.matchReasons.map((r) => r.text).join(' · ')}</dd>
            </>
          ) : null}
        </dl>
      </section>

      <section>
        <h3 style={{ marginBottom: 8 }}>שיוך ללקוח</h3>
        <div className="stack" style={{ gap: 8 }}>
          {ingestion.candidates.length ? (
            ingestion.candidates.map((c) => (
              <button
                type="button"
                key={c.customerId}
                className={`candidate ${mode === 'candidate' && candidate === c.customerId ? 'selected' : ''}`}
                onClick={() => {
                  setMode('candidate');
                  setCandidate(c.customerId);
                }}
                aria-pressed={mode === 'candidate' && candidate === c.customerId}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="strong ellipsis">{c.businessName}</div>
                  <div className="small muted">{c.reasons.join(' · ')}</div>
                </div>
                <Confidence value={c.score} />
              </button>
            ))
          ) : !presetCustomerId ? (
            <div className="muted small">{t.review.noSuggestions}</div>
          ) : null}

          <div className="segmented" role="group" style={{ alignSelf: 'flex-start' }}>
            <button type="button" className={mode === 'other' ? 'active' : ''} onClick={() => setMode('other')}>{t.review.chooseOther}</button>
            <button type="button" className={mode === 'new' ? 'active' : ''} onClick={() => setMode('new')}>{t.review.createNew}</button>
          </div>

          {mode === 'other' ? <CustomerPicker value={other} onChange={setOther} /> : null}
          {mode === 'new' ? (
            <div className="panel panel-body">
              <div className="form-grid">
                <Field label={t.customerForm.businessName} required className="span-2">
                  <Input value={newC.businessName} onChange={(e) => setNewC({ ...newC, businessName: e.target.value })} />
                </Field>
                <Field label={t.customerForm.companyNumber}>
                  <Input value={newC.companyNumber} onChange={(e) => setNewC({ ...newC, companyNumber: e.target.value })} ltr inputMode="numeric" />
                </Field>
                <Field label={t.customerForm.terms}>
                  <Select value={newC.paymentTerms} onChange={(e) => setNewC({ ...newC, paymentTerms: e.target.value as PaymentTerms })}>
                    {(Object.keys(t.enums.paymentTerms) as PaymentTerms[]).map((k) => (
                      <option key={k} value={k}>{t.enums.paymentTerms[k]}</option>
                    ))}
                  </Select>
                </Field>
                {newC.paymentTerms === 'EOM_FIXED_DAY' ? (
                  <Field label={t.customerForm.paymentDay} required>
                    <Input type="number" min={1} max={31} value={newC.paymentDay} onChange={(e) => setNewC({ ...newC, paymentDay: e.target.value })} ltr />
                  </Field>
                ) : null}
                <Field label="סניף / מחלקה של" hint="לדוגמה: בית ספר תחת העירייה. ניתן להשאיר ריק" className="span-2">
                  <div className="row">
                    <div style={{ flex: 1 }}>
                      <CustomerPicker value={newC.parentId} onChange={(v) => setNewC({ ...newC, parentId: v })} onlyTopLevel placeholder="לקוח עצמאי" />
                    </div>
                    {newC.parentId ? <Button size="sm" variant="ghost" onClick={() => setNewC({ ...newC, parentId: null })}>הסרה</Button> : null}
                  </div>
                </Field>
              </div>
            </div>
          ) : null}

          {dups ? (
            <Callout tone="warn" title={t.customerForm.duplicateTitle}>
              <div>{t.customerForm.duplicateBody}</div>
              <ul style={{ margin: '6px 0', paddingInlineStart: 18 }}>
                {dups.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() => {
                        setMode('other');
                        setOther(d.id);
                        setDups(null);
                      }}
                    >
                      שיוך ל"{d.businessName}"
                    </button>{' '}
                    <span className="muted small">{d.reason}</span>
                  </li>
                ))}
              </ul>
              <Button size="sm" onClick={() => submit(true)}>{t.customerForm.createAnyway}</Button>
            </Callout>
          ) : null}
        </div>
      </section>

      <section>
        <h3 style={{ marginBottom: 8 }}>{t.review.fields}</h3>
        <div className="form-grid">
          <Field label={t.docs.cols.type} required>
            <Select value={fields.type} onChange={(e) => setFields({ ...fields, type: e.target.value as DocumentType })}>
              {DOC_TYPES.map((d) => (
                <option key={d} value={d}>{t.enums.docType[d]}</option>
              ))}
            </Select>
          </Field>
          <Field label={t.docs.cols.number}>
            <Input value={fields.number} onChange={set('number')} ltr />
          </Field>
          <Field label={t.docs.cols.date}>
            <Input type="date" value={fields.issueDate} onChange={set('issueDate')} />
          </Field>
          <Field label={t.docs.cols.subtotal}>
            <Input inputMode="decimal" value={fields.subtotal} onChange={set('subtotal')} ltr />
          </Field>
          <Field label={t.docs.cols.vat}>
            <Input inputMode="decimal" value={fields.vat} onChange={set('vat')} ltr />
          </Field>
          <Field label={t.docs.cols.total} error={badNumber ? 'יש להזין מספרים בלבד' : sumCheck}>
            <Input inputMode="decimal" value={fields.total} onChange={set('total')} ltr />
          </Field>
          {fields.type === 'delivery_note' ? (
            <Field label={t.docs.cols.address} className="span-2">
              <Input value={fields.deliveryAddress} onChange={set('deliveryAddress')} />
            </Field>
          ) : null}
          <Field label={t.docs.cols.description} className="span-2">
            <Input value={fields.description} onChange={set('description')} />
          </Field>
        </div>
      </section>

      <div className="stack" style={{ gap: 6 }}>
        {ex?.customerName && mode !== 'new' ? <Checkbox checked={learnAlias} onChange={setLearnAlias}>{t.review.learnAlias}</Checkbox> : null}
        {ex?.companyNumber && mode !== 'new' ? <Checkbox checked={setCN} onChange={setSetCN}>{t.review.setCompanyNumber}</Checkbox> : null}
      </div>

      <div className="row wrap">
        <Button variant="primary" onClick={() => submit(false)} disabled={!canSubmit} loading={busy}>{t.review.assign}</Button>
        {onIgnore ? <Button onClick={ignore}>{t.review.ignore}</Button> : null}
        {ingestion.duplicateOfDocumentId ? (
          <Link to={`?doc=${ingestion.duplicateOfDocumentId}`} className="btn ghost">פתיחת המסמך הקיים</Link>
        ) : null}
      </div>
    </div>
  );
}

/** Same company number but a different name usually means a branch: pre-select the parent. */
function initialNew(i: Ingestion) {
  const ex = i.extracted;
  const sameEntity = i.candidates.find((c) => c.reasons.some((r) => r.includes('ייתכן שזה סניף')));
  return {
    businessName: ex?.customerName ?? '',
    companyNumber: ex?.companyNumber ?? '',
    paymentTerms: 'EOM_30' as PaymentTerms,
    paymentDay: '',
    parentId: sameEntity?.customerId ?? (null as number | null),
  };
}

function initialFields(i: Ingestion) {
  const ex = i.extracted;
  const s = (v: number | null | undefined) => (v == null ? '' : String(v));
  return {
    type: (ex?.type ?? i.detectedType ?? 'tax_invoice') as DocumentType,
    number: ex?.number ?? i.detectedNumber ?? '',
    issueDate: ex?.issueDate ?? '',
    subtotal: s(ex?.subtotal),
    vat: s(ex?.vat),
    total: s(ex?.total),
    description: ex?.description ?? '',
    deliveryAddress: ex?.customerAddress ?? '',
  };
}
